import { FaArrowLeft } from "react-icons/fa6";
import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { toast } from "react-toastify";

import { TransactionDetailModalInput, TransactionMetadata } from "./types";
import DataUpLogo from "../../assets/images/DataUpLogo.svg";
import { useAppSelector } from "../../hooks/hooks";
import { IoClose } from "react-icons/io5";
import { FiShare2 } from "react-icons/fi";

const isInReactNativeWebView = (): boolean =>
  typeof window !== "undefined" &&
  Boolean((window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView);

const postToNative = (payload: Record<string, unknown>) => {
  const bridge = (window as unknown as {
    ReactNativeWebView?: { postMessage: (msg: string) => void };
  }).ReactNativeWebView;
  bridge?.postMessage(JSON.stringify(payload));
};

export const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transaction,
  formatAmountWithSymbol,
  formatDate,
  formatTime,
  getCurrencySymbol,
}: TransactionDetailModalInput) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isResendingReceipt, setIsResendingReceipt] = useState(false);
  const token = useAppSelector((state) => state.auth.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const documentTitle = `Receipt_${transaction?.id || "Transaction"}`;

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle,
  });

  const buildReceiptHtml = (): string => {
    if (!contentRef.current) return "";
    const inner = contentRef.current.outerHTML;
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${documentTitle}</title>
<script src="https://cdn.tailwindcss.com"></script>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; margin: 0; padding: 16px; color: #111; background: #fff; }
  .no-print { display: none !important; }
  img { max-width: 100%; }
</style>
</head>
<body>${inner}</body>
</html>`;
  };

  const handlePrintClick = () => {
    if (isInReactNativeWebView()) {
      postToNative({
        action: "print",
        html: buildReceiptHtml(),
        filename: documentTitle,
      });
      return;
    }
    handlePrint();
  };

  const handleShareClick = async () => {
    if (isInReactNativeWebView()) {
      postToNative({
        action: "share",
        html: buildReceiptHtml(),
        filename: documentTitle,
      });
      return;
    }
    const navShare = (navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>;
    }).share;
    if (navShare) {
      try {
        await navShare.call(navigator, {
          title: documentTitle,
          text: `Transaction Receipt #${transaction?.id ?? ""}`,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
        return;
      } catch {
        // user cancelled or share failed — fall through to print dialog
      }
    }
    handlePrint();
  };

  const isCardTransaction = (type?: string | number, typeText?: string) => {
    const normalizedTypeText = String(typeText || "").toUpperCase();
    const numericType = Number(type);

    return (
      normalizedTypeText === "GAMECARD" ||
      normalizedTypeText === "GIFTCARD" ||
      numericType === 5 ||
      numericType === 6
    );
  };

  const getRecipientLabel = (type: string) => {
    const labels: Record<string, string> = {
      CABLE: "Smartcard No:",
      DATA: "Phone No",
      AIRTIME: "Phone No",
      TOPUP: "UID",
      GAMECARD: "Email ",
      GIFTCARD: "Email ",
      ELECTRIC: "Meter No",
      "DG COINS PURCHASE": "Ref No",
    };

    return labels[type] || "Account/Ref No";
  };
  if (!transaction) return null;
  if (!isOpen) return null;

  const metadata: TransactionMetadata =
    typeof transaction.metadata === "string"
      ? (() => {
          try {
            const parsed = JSON.parse(transaction.metadata);
            return parsed && typeof parsed === "object" ? parsed : {};
          } catch {
            return {};
          }
        })()
      : transaction.metadata && typeof transaction.metadata === "object"
        ? transaction.metadata
        : {};

  const formatDisplayValue = (value?: string | number) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const stringValue = String(value).trim();
    const match = stringValue.match(/^([^0-9.-]*)(-?\d+(?:\.\d+)?)(.*)$/);

    if (!match) {
      return stringValue;
    }

    const [, prefix, numericPart, suffix] = match;
    const formattedNumber = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(numericPart));

    return `${prefix}${formattedNumber}${suffix}`;
  };

  const statusStyles: Record<string, string> = {
    PENDING: "bg-orange-100 text-orange-500",
    SUCCESSFUL: "bg-green-100 text-green-500",
    PAID: "bg-green-100 text-green-500",
    REVERSED: "bg-blue-100 text-blue-500",
    FAILED: "bg-red-100 text-red-500",
    QUEUED: "bg-yellow-200 text-yellow-600",
    PROCESSING: "bg-yellow-100 text-yellow-500",
  };

  const statusClassName =
    statusStyles[transaction.status_text] || "bg-orange-100 text-orange-500";
  const isSuccessfulTransaction =
    String(transaction.status_text || "").toUpperCase() === "SUCCESSFUL";
  const canResendReceipt =
    isSuccessfulTransaction &&
    isCardTransaction(transaction.type, transaction.type_text);

  const handleResendReceipt = async () => {
    if (!transaction?.id || !token || !API_URL || isResendingReceipt) return;

    try {
      setIsResendingReceipt(true);

      const response = await axios.post(
        `${API_URL}/resend-transaction-receipt`,
        { transaction_id: transaction.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const recipientEmail =
        response.data?.data?.recipient_email || transaction.unique_number;

      toast.success(`Receipt resent to ${recipientEmail}`);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to resend transaction receipt. Please try again.",
      );
    } finally {
      setIsResendingReceipt(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 font-satoshi">
      {/* Clickable Backdrop to close */}
      <div className="fixed inset-0" onClick={onClose} />

      {/* New Modal Container */}
      <div className="relative w-full max-w-lg bg-white dark:bg-black rounded-2xl shadow-2xl my-8 overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Floating Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[70] flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all no-print"
        >
          <IoClose size={24} />
        </button>

        {/* Start of Receipt Content */}
        <div ref={contentRef} className="print-container">
          <div className="flex flex-col items-center justify-center pt-8 pb-4 border-b border-dashed dark:border-gray-700">
            <img
              src={DataUpLogo}
              alt="DataUp Logo"
              className="h-10 w-auto object-contain mb-2"
            />
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
              Official Transaction Receipt
            </p>
          </div>

          <div className="flex items-center p-4 border-b dark:border-gray-700 no-print">
            <button onClick={onClose} className="mr-2">
              <FaArrowLeft
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            </button>
            <h2 className="text-lg font-semibold">Transaction Details</h2>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl mt-4 mb-6 space-y-3 mx-4">
            <div className="flex justify-between items-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Transaction Status
              </p>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusClassName}`}
              >
                {transaction.status_text}
              </span>
            </div>

            {transaction.unique_number && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {getRecipientLabel(transaction.type_text)}
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {transaction.unique_number}
                </p>
              </div>
            )}

            {metadata.customer_name && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Customer Name
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {metadata.customer_name}
                </p>
              </div>
            )}

            {metadata.customer_address && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Customer Address
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {metadata.customer_address}
                </p>
              </div>
            )}

            {metadata.token && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Token
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {metadata.token}
                </p>
              </div>
            )}

            {metadata.card_pin && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Card Pin
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {metadata.card_pin}
                </p>
              </div>
            )}

            {metadata.units && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Units
                </p>
                <p className="font-semibold text-sm dark:text-white truncate ml-4">
                  {metadata.units}
                </p>
              </div>
            )}
          </div>

          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-semibold dark:text-white">
                  Order Details
                </h3>
                <div className="flex py-2">
                  <p className="text-gray-500">Order ID: </p>
                  <p className="font-medium dark:text-white ml-2">
                    #{transaction.id}
                  </p>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(transaction.created_at)} {"/"}{" "}
                  {formatTime(transaction.created_at)}
                </p>
              </div>
              {/* Order Type Badge */}
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 uppercase">
                {transaction.type_text}
              </span>
            </div>

            {/* Transaction items */}
            <div className="space-y-4 mb-8">
              {transaction.items_details &&
                transaction.items_details.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b pb-4 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      {/* Visual Indicator: Image or Icon */}
                      <div className="relative">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            crossOrigin="anonymous"
                            className="w-11 h-11 rounded-lg object-cover border dark:border-gray-600"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500">
                            <span className="text-xl">💳</span>
                          </div>
                        )}
                      </div>

                      {/* Item & User Detail */}
                      <div>
                        <p className="text-sm font-bold text-gray-800 dark:text-white">
                          {item.name || transaction.type_text}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded font-medium uppercase">
                            User
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400 font-bold">
                            {item.username || "Guest User"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Pricing Column */}
                    <div className="text-right">
                      <p className="text-[11px] text-blue-500 font-semibold">
                        {formatDisplayValue(item.currency_amount)}
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {formatDisplayValue(item.price)}
                      </p>
                    </div>
                  </div>
                ))}

              {/* If no item details are provided, show placeholder */}
              {(!transaction.items_details ||
                transaction.items_details.length === 0) && (
                <div className="py-4 text-center text-gray-500">
                  No detailed item information available
                </div>
              )}
            </div>

            {/* Transaction summary */}
            <div className="space-y-2 border-t pt-4 dark:border-gray-700">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">
                  Subtotal
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {formatAmountWithSymbol(
                    transaction.subtotal ??
                      transaction.price ??
                      transaction.amount,
                    getCurrencySymbol(transaction.currency_id) ||
                      transaction.currency_symbol ||
                      transaction.currency?.symbol ||
                      "",
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Fee</span>
                <span className="text-gray-500 dark:text-gray-400">
                  {getCurrencySymbol(transaction.currency_id)}
                  {transaction.fee || "0"}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2 dark:border-gray-700">
                <span className="font-semibold text-gray-500 dark:text-gray-400">
                  Total
                </span>
                <div className="flex items-center">
                  {transaction.coin_amount && (
                    <span className="text-yellow-500 mr-2">
                      🪙 {transaction.coin_amount}
                    </span>
                  )}
                  <span className="font-semibold text-gray-500 dark:text-gray-400">
                    {formatAmountWithSymbol(
                      transaction.price ?? transaction.amount,
                      getCurrencySymbol(transaction.currency_id) ||
                        transaction.currency_symbol ||
                        transaction.currency?.symbol ||
                        "",
                    )}
                  </span>
                </div>
              </div>

              {/* Receipt Action Buttons */}
              <div className="flex items-center justify-center gap-3 mt-6 no-print flex-wrap">
                {canResendReceipt && (
                  <button
                    onClick={handleResendReceipt}
                    disabled={isResendingReceipt}
                    className="border border-pry-500 text-pry-500 rounded-md lg:text-lg p-2 px-6 font-semibold hover:bg-pry-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isResendingReceipt
                      ? "Resending..."
                      : "Resend Card To Email"}
                  </button>
                )}
                <button
                  onClick={handlePrintClick}
                  className="bg-pry-500 rounded-md lg:text-lg p-2 px-6 text-white font-semibold hover:bg-pry-600 transition-colors"
                >
                  Print Receipt
                </button>
                <button
                  onClick={handleShareClick}
                  className="border border-pry-500 text-pry-500 rounded-md lg:text-lg p-2 px-6 font-semibold hover:bg-pry-50 transition-colors flex items-center gap-2"
                >
                  <FiShare2 size={18} />
                  Share Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-container { 
            padding: 20px; 
            color: black !important; 
            background: white !important; 
          }
          * { color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};
